<template>
  <div v-rect-loading="loading" class="offer-letter-container" :style="rootStyle">
    <div class="container">
      <div class="download-btn">
        <i v-rect-loading="downloading" class="btn iconfont icona-ResumeDownload" :class="isPreview && 'preview'" @click="handleExportPDF"></i>
      </div>
      <div v-html="displayOfferLetterContent" class="offer-content" ref="offerContentRef"></div>
    </div>
    <div class="footer-btn">
      <div class="btn-group">
        <div class="btn cancel" :class="isPreview && 'preview'" @click="handleReject">{{ $t(`${i18nPrefix}.btn.rejectOffer`) }}</div>
        <div class="btn save" :class="isPreview && 'preview'" @click="handleAccept">{{ $t('common.btn.accept') }}</div>
      </div>
    </div>
    <div v-if="showSignDialog" class="sign-pad" :style="{ height: `${signHeight}px` }">
      <div class="bottom">
        <div class="footer-btn">
          <div class="btn cancel" @click="handleCancelSign">
            <span class="label">{{ $t('common.btn.cancel') }}</span>
          </div>
          <div class="btn submit" @click="handleSaveSign">
            <span class="label">{{ $t('common.btn.submit') }}</span>
          </div>
        </div>
      </div>
      <SignaturePad needRotate ref="signRef" :width="`${signWidth}px`" :height="`${signHeight - 60}px`"></SignaturePad>
    </div>
    <van-popup v-rect-loading="acceptLoading" v-model="showAcceptDialog" class="operate-popup accept-offer-dialog" closeable round position="bottom">
      <div class="info accept-info">
        <div class="title">{{ $t(`${i18nPrefix}.label.acceptOfferTitle`) }}</div>
        <WrappedForm
          ref="acceptForm"
          class="field-info accept-field-info"
          label-width="220px"
          :formLine="acceptFormLines"
          :data="acceptInfo"
        >
          <template #signSlot>
            <div v-if="!showSignDialog && !acceptInfo.signature" class="sign-mark" @click="handleSignMarkClick">
              <div class="clear-btn" @click="handleCancelSign"><i class="icon iconfont iconRefresh"></i></div>
            </div>
            <div v-if="acceptInfo.signature" class="sign-img">
              <div class="clear-btn" @click="handleCancelSign"><i class="icon iconfont iconRefresh"></i></div>
              <img :src="acceptInfo.signature" />
            </div>
            <div v-show="showSignTip" class="sign-tip">{{ $t('common.message.requiredFormField') }}</div>
          </template>
        </WrappedForm>
        <div class="footer-btn">
          <div class="btn cancel" @click="handleCancelAccept">{{ $t('common.btn.cancel') }}</div>
          <div class="btn submit" @click="handleSaveAccept">{{ $t('common.btn.submit') }}</div>
        </div>
      </div>
    </van-popup>
    <van-popup v-rect-loading="rejectLoading" v-model="showRejectDialog" class="operate-popup reject-offer-dialog" closeable round position="bottom">
      <div class="info reject-info">
        <div class="title">{{ $t(`${i18nPrefix}.label.rejectOfferTitle`) }}</div>
        <WrappedForm
          ref="rejectForm"
          class="field-info reject-field-info"
          label-width="220px"
          :formLine="rejectFormLines"
          :data="rejectInfo"
        >
        </WrappedForm>
        <div class="footer-btn">
          <div class="btn cancel" @click="handleCancelReject">{{ $t('common.btn.cancel') }}</div>
          <div class="btn submit" @click="handleSaveReject">{{ $t('common.btn.submit') }}</div>
        </div>
      </div>
    </van-popup>
    <MobileCommonDialog
      class="submit-accept-success-dialog"
      hiddenLogo
      hideFooter
      closeOnSave
      :show.sync="acceptSuccessDialog.show"
      :loading="acceptSuccessDialog.loading"
    >
      <template #content>
        <div class="accept-success-container">
          <i class="result-icon iconfont iconcompleted"></i>
          <span class="title-label">{{ $t(`${i18nPrefix}.label.acceptOfferSuccess`) }}</span>
          <el-button class="button view-offer-letter" type="primary" @click="handelToViewOfferLetter">{{ $t(`${i18nPrefix}.btn.viewAllOffer`) }}</el-button>
          <el-button class="button download-offer" @click="handleExportPDF(false)">
            <span class="label">{{ $t(`${i18nPrefix}.btn.downloadOfferLetter`) }}</span>
            <i class="icon el-icon-download"></i>
          </el-button>
        </div>
      </template>
    </MobileCommonDialog>
    <MobileCommonDialog
      class="submit-reject-success-dialog"
      hiddenLogo
      hideFooter
      closeOnSave
      :show.sync="rejectSuccessDialog.show"
    >
      <template #content>
        <div class="reject-success-container">
          <i class="result-icon iconfont iconcompleted"></i>
          <span class="title-label">{{ $t(`${i18nPrefix}.label.rejectOfferSuccess`) }}</span>
        <el-button class="button view-offer-letter" type="primary" @click="handelToPersonalHome">{{ $t(`${i18nPrefix}.btn.toPersonalHome`) }}</el-button>
        </div>
      </template>
    </MobileCommonDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import SignaturePad from '@/components/common/sign/signaturePad.vue'
import WrappedForm from '@/components/mobile/resumeForm.vue'
import MobileCommonDialog from '@/components/dialog/mobileCommonDialog.vue'
import { getFilePath, uploadBase64Src } from '@/utils/qiniu'
import { getI18nPropertyValue, addI18nProperty } from '@/i18n'
import { AsyncTask } from '@/utils/common'
import { OFFER_LETTER_TEMPLATE_FIELD_TYPE } from '@/constant/company'
import mixin from '../mixin'

export default {
  mixins: [mixin],
  components: {
    SignaturePad,
    WrappedForm,
    MobileCommonDialog
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false
    },
    previewOfferLetterInfo: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      i18nPrefix: 'personal.offerLetter',
      loading: false,
      downloading: false,
      company: {},
      showAcceptDialog: false,
      acceptLoading: false,
      showSignTip: false,
      showRejectDialog: false,
      rejectLoading: false,
      templateFieldList: [],
      acceptInfo: {},
      rejectCaseList: [],
      rejectInfo: {
        rejectCase: null,
        caseComment: null
      },
      inviteRecordInfo: {
        offer_letter_content: null
      },
      acceptSuccessDialog: {
        show: false,
        loading: false
      },
      rejectSuccessDialog: {
        show: false
      },
      // mobile 横屏签名，签名框的宽度需要设置，84 是按钮区域宽度，24 是签名框 margin-right
      signWidth: window.visualViewport.width - 84 - 24,
      signHeight: window.innerHeight,
      showSignDialog: false
    }
  },
  computed: {
    jobId () {
      return this.$route.query.job_id
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    inviteId () {
      return this.$route.query.invite_id
    },
    companyId () {
      return this.$route.query.company_id
    },
    rootStyle () {
      const style = {}
      if (this.company.custom_color) {
        style['--custom-color'] = this.company.custom_color
      }
      return style
    },
    displayOfferLetterContent () {
      if (this.inviteRecordInfo.offer_letter_content) {
        return this.formatContent(this.inviteRecordInfo.offer_letter_content, this.acceptInfo)
      }
      return ''
    },
    templateContentFields () {
      const content = this.inviteRecordInfo.offer_letter_content
      if (!content) {
        return []
      }
      const allKeys = this.getTemplateAllPlaceholder(content)
      const templateFields = _.chain(this.templateFieldList).filter(item => _.includes(allKeys, item.key)).map(item => {
        item.order = _.findIndex(allKeys, key => key === item.key)
        return item
      }).orderBy('order').value()
      return templateFields
    },
    acceptFormLines () {
      const lines = []
      const content = this.inviteRecordInfo.offer_letter_content
      if (!content) {
        return lines
      }
      const templateFieldList = _.filter(this.templateContentFields, { type: 'CANDIDATE' })
      _.forEach(templateFieldList, item => {
        const key = item.key
        const label = getI18nPropertyValue(item, 'name_cn', 'name_en')
        const line = {
          name: key,
          label: label,
          elType: 'input',
          required: true,
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        }
        const valueType = item.value_type
        if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.SELECT) {
          line.elType = 'select'
          line.notSearch = true
          line.options = {
            data: item.option_list
          }
        } else if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.SINGLE) {
          line.elType = 'select'
          line.options = {
            data: item.option_list
          }
        } else if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.TEXT) {

        } else if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.TEXTAREA) {
          line.elType = 'question-text'
        } else if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.DATE) {
          line.elType = 'date'
        } else if (valueType === OFFER_LETTER_TEMPLATE_FIELD_TYPE.SIGN) {
          line.slotName = 'signSlot'
          line.title = label
          line.signature = {
          }
        }
        lines.push(line)
      })
      return lines
    },
    rejectFormLines () {
      return [
        {
          name: 'rejectCase',
          notSearch: true,
          elType: 'select',
          options: {
            data: this.rejectCaseList
          },
          label: this.$t(`${this.i18nPrefix}.label.rejectCase`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        {
          name: 'caseComment',
          elType: 'text',
          autosize: {
            minHeight: '42px'
          },
          label: this.$t(`${this.i18nPrefix}.label.rejectCaseComment`)
        }
      ]
    }
  },
  created () {
    this.initPage()
  },
  mounted () {
    this.resetAcceptInfo()
  },
  methods: {
    ...mapActions('company', ['getCompanyBaseInfo']),
    ...mapActions('candidateStatus', ['getStatusChangeReasonList']),
    ...mapActions('application', [
      'createExportCandidateOfferLetterInfoTask',
      'getExportCandidateOfferLetterTaskStatus'
    ]),
    ...mapActions('offerLetter', ['getOfferLetterInviteRecordInfoById', 'saveCandidateOfferLetterOperate', 'getOfferLetterTemplateFieldList']),
    async initPage () {
      await this.loadTemplateFiledList()
      if (this.isPreview) {
        this.inviteRecordInfo = this.previewOfferLetterInfo
        this.resetAcceptInfo()
        this.$nextTick(() => {
          this.resetHeadImgStyle()
        })
      } else {
        this.loadCompanyInfo()
        this.loadInviteOfferLetterInfo()
        this.loadRejectOfferCaseList()
      }
    },
    async loadCompanyInfo () {
      const resp = await this.getCompanyBaseInfo({ id: this.companyId, params: { simple_info: 1 } })
      if (resp) {
        this.company = addI18nProperty(resp, 'i18nName', 'cn_name', 'name')
      }
    },
    async loadInviteOfferLetterInfo () {
      this.loading = true
      try {
        const params = {
          invite_id: this.inviteId,
          job_id: this.jobId
        }
        const resp = await this.getOfferLetterInviteRecordInfoById(params)
        if (resp) {
          this.inviteRecordInfo = resp
          this.resetAcceptInfo()
          this.$nextTick(() => {
            this.resetHeadImgStyle()
          })
        }
      } finally {
        this.loading = false
      }
    },
    async loadTemplateFiledList () {
      const resp = await this.getOfferLetterTemplateFieldList({ company_id: this.companyId })
      if (resp) {
        _.forEach(resp, item => {
          _.forEach(item.option_list, option => {
            addI18nProperty(option, 'label', 'name_cn', 'name_en')
          })
        })
        this.allTemplateFiledList = resp
        this.templateFieldList = resp
      }
    },
    handleSignMarkClick () {
      this.showAcceptDialog = false
      this.showSignDialog = true
      this.acceptInfo.signature = null
    },
    handleCancelSign () {
      this.showSignDialog = false
      this.showAcceptDialog = true
      this.acceptInfo.signature = null
      this.handleClearSign()
    },
    async handleSaveSign () {
      if (this.$refs.signRef && !this.acceptInfo.signature) {
        const signData = await this.$refs.signRef.toDataURL()
        this.acceptInfo.signature = await this.uploadSign(signData)
      }
      this.showSignDialog = false
      this.showAcceptDialog = true
    },
    resetAcceptInfo () {
      _.forEach(this.templateContentFields, field => {
        if (!_.has(this.acceptInfo, field.key)) {
          this.$set(this.acceptInfo, field.key, null)
        }
      })
    },
    resetHeadImgStyle () {
      const offerContentRef = this.$refs.offerContentRef
      const headerImgEl = offerContentRef.querySelector('.offer-letter-head-img')
      if (!headerImgEl || !offerContentRef) {
        return
      }
      const offerContentStyle = window.getComputedStyle(offerContentRef)
      const offerContentWidth = offerContentRef.clientWidth - _.parseInt(offerContentStyle.paddingLeft) - _.parseInt(offerContentStyle.paddingRight)
      const ratios = offerContentWidth / 580
      const headerImgWidth = headerImgEl.clientWidth * ratios
      const headerImgHeight = headerImgEl.clientHeight * ratios
      headerImgEl.style.width = `${headerImgWidth}px`
      headerImgEl.style.height = `${headerImgHeight}px`
    },
    async loadRejectOfferCaseList () {
      const resp = await this.getStatusChangeReasonList({
        status_key: 'Offer Rejected',
        company_id: this.companyId
      })
      if (resp) {
        this.rejectCaseList = _.map(resp, item => addI18nProperty(item, 'label', 'name_cn', 'name_en'))
      }
    },
    handleAccept () {
      if (this.isPreview) {
        return
      }
      this.showAcceptDialog = true
    },
    handleReject () {
      if (this.isPreview) {
        return
      }
      this.showRejectDialog = true
    },
    handleCancelAccept () {
      this.handleClearSign()
      this.showAcceptDialog = false
    },
    handleClearSign () {
      if (this.$refs.signRef && !this.$refs.signRef.isEmpty()) {
        this.$refs.signRef.clear()
      }
    },
    async uploadSign (base64Src) {
      const fileName = `${this.candidateId}_${this.jobId}_${new Date().valueOf()}_sign.png`
      const fileKey = `offerLetter/candidateSign/${this.candidateId}/${this.jobId}/${fileName}`
      const resp = await uploadBase64Src(fileKey, base64Src)
      if (resp) {
        this.fileKey = resp.key
        const filePath = getFilePath(this.fileKey)
        return filePath
      }
    },
    async handleSaveAccept () {
      if (!await this.$refs.acceptForm.submit()) {
        return
      }
      if (_.has(this.acceptInfo, 'signature') && !this.acceptInfo.signature) {
        this.showSignTip = true
        return
      }
      this.acceptLoading = true
      try {
        const acceptInfo = this.formatOfferLetterInfo(this.acceptInfo)
        this.inviteRecordInfo.offer_letter_content = this.formatContent(this.inviteRecordInfo.offer_letter_content, this.acceptInfo)
        const info = {
          invite_id: this.inviteId,
          candidate_id: this.candidateId,
          job_id: this.jobId,
          status: 1,
          accept_info: acceptInfo,
          offer_letter_content: this.inviteRecordInfo.offer_letter_content
        }
        await this.saveCandidateOfferLetterOperate(info)
        this.acceptSuccessDialog.show = true
      } finally {
        this.acceptLoading = false
        this.showAcceptDialog = false
      }
    },
    handleCancelReject () {
      this.showRejectDialog = false
    },
    async handleSaveReject () {
      if (!await this.$refs.rejectForm.submit()) {
        return
      }
      this.rejectLoading = true
      try {
        const info = {
          invite_id: this.inviteId,
          candidate_id: this.candidateId,
          job_id: this.jobId,
          status: 2,
          reject_case_id: this.rejectInfo.rejectCase,
          reject_comment: this.rejectInfo.caseComment
        }
        await this.saveCandidateOfferLetterOperate(info)
        this.rejectSuccessDialog.show = true
      } finally {
        this.rejectLoading = false
        this.showRejectDialog = false
      }
    },
    handelToPersonalHome () {
      this.$router.push(`/recruiting/personal/home?company_id=${this.companyId}`)
    },
    handleExportPDF (defaultLoading = true) {
      if (this.isPreview) {
        return
      }
      if (defaultLoading) {
        this.downloading = true
      } else {
        this.acceptSuccessDialog.loading = true
      }
      const exportOfferLetterPdfStatus = {
        finished: false,
        task: null
      }
      exportOfferLetterPdfStatus.finished = false
      exportOfferLetterPdfStatus.task = new AsyncTask(() => this.createExportCandidateOfferLetterInfoTask({
        candidate_id: this.candidateId,
        job_id: this.jobId
      }), (taskId) => this.getExportCandidateOfferLetterTaskStatus({
        task_id: taskId
      }), (taskId) => {
        // 部分手机使用 window.location.href 跳转下载会失败，所以使用 a 标签模拟点击下载
        const downloadEl = document.createElement('a')
        downloadEl.href = this.$DjangoBaseUrl(`/django_rest/company/job/application/export/offer-letter/?task_id=${taskId}&download=1`)
        downloadEl.click()
        exportOfferLetterPdfStatus.finished = false
        exportOfferLetterPdfStatus.task = null
        this.downloading = false
        this.acceptSuccessDialog.loading = false
      })
    },
    handelToViewOfferLetter () {
      this.$router.replace({ path: `/recruiting/personal/offer-letter/${this.inviteId}/detail`, query: { company_id: this.companyId } })
    }
  }
}
</script>

<style lang="scss" scoped>
.offer-letter-container {
  position: relative;
  .container {
    margin: 0 auto;

    .offer-content {
      padding: 0px 44px 54px 44px;
    }

    .download-btn {
      padding: 30px 44px 0 44px;
      align-items: center;
      display: flex;
      justify-content: flex-end;
      .btn {
        font-size: 20px;
        color: #0096cc;
        cursor: pointer;
      }
      ::v-deep {
        .el-loading-spinner {
          margin-top: -12px;

          .loading-spinner {
            .spinner {
              width: 20px;
              height: 20px;

              >div {
                width: 3px;
              }
            }
          }
        }
      }
      .preview {
        cursor: not-allowed;
      }
    }

  }

  .footer-btn {
    position: sticky;
    bottom: 0;
    background-color: #fff;
    border-top: 1px solid #c0c4cc;
    padding: 10px 0 20px 0;
    .btn-group {
      margin: 4px 20px 4px 20px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      text-align: center;
      .btn {
        width: 50%;
        margin-top: 10px;
        padding: 10px;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
        word-break: keep-all;
        white-space: nowrap;
        border: 1px solid #0096cc;
      }
      .cancel {
        margin-right: 14px;
        color: #0096cc;
      }
      .save {
        color: #ffffff;
        background: #0096cc;
      }
      .preview {
        cursor: not-allowed;
      }
    }
  }

  .sign-pad {
    display: flex;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 9999;
    background-color: #fff;
    user-select: none;
    touch-action: manipulation;
    .signature-wrapper {
      margin: 30px 24px 30px 0;
      flex: 1;
    }

    .bottom {
      display: flex;
      align-items: center;
      margin: 34px 16px 30px 16px;
      flex-direction: column;
      justify-content: flex-end;

      .tip {
        flex: 1;
        color: #606266;
        font-size: 16px;
        white-space: nowrap;
        word-break: break-word;
        text-overflow: ellipsis;
        overflow: hidden;
        padding-top: 8px;
        .label {
          width: 32px;
          display: inline-block;
          transform: rotate(90deg);
        }
      }

      .footer-btn {
        height: 50%;
        position: relative;
        border: 0;
        display: flex;
        margin: 0;
        width: 100%;
        flex-direction: column;
        padding: 0;
        .btn {
          margin-top: 10px;
          padding: 10px;
          border-radius: 6px;
          background: #f2f2f2;
          font-size: 16px;
          cursor: pointer;
          word-break: keep-all;
          white-space: nowrap;
          display: flex;
          align-items: center;
          .label {
            width: 32px;
            display: inline-block;
            transform: rotate(90deg);
          }
        }

        .cancel {
          height: 50%;
          text-align: center;
        }
        .submit {
          height: 50%;
          color: #ffffff;
          background: #0096cc;
          text-align: center;
        }
      }
    }
  }

  .operate-popup {
    .info {
      .title {
        padding: 20px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
      }
      .field-info {
        padding: 20px;
        ::v-deep {
          .title {
            color: #909399;
          }
        }
      }
      .footer-btn {
        padding: 0 20px 24px 20px;
        display: flex;
        align-items: center;
        text-align: center;
        .btn {
          margin-top: 10px;
          padding: 10px;
          border-radius: 6px;
          background: #f2f2f2;
          font-size: 16px;
          cursor: pointer;
          word-break: keep-all;
          white-space: nowrap;
        }

        .cancel {
          width: 50%;
          margin-right: 14px;
        }
        .submit {
          width: 50%;
          color: #ffffff;
          background: #0096cc;
        }
      }
    }
  }

  .accept-offer-dialog {
    .accept-info {
      .title {
      }
      .accept-field-info {
        .sign-mark {
          color: #fff;
          background-color: #fff;
          opacity: 0.8;
          width: 100%;
          height: 200px;
          border: 1px solid #c0c4cc;
          position: relative;
          .clear-btn {
            color: #0096cc;
            position: absolute;
            right: 10px;
            top: 8px;
            .icon {
              font-size: 18px;
            }
          }
        }

        .sign-img {
          width: 100%;
          height: 200px;
          border: 1px solid #c0c4cc;
          position: relative;
          .clear-btn {
            color: #0096cc;
            position: absolute;
            right: 10px;
            top: 8px;
            .icon {
              font-size: 18px;
            }
          }
          img {
            width: 100%;
            height: auto;
          }
        }

        .sign-tip {
          color: #F56C6C;
          font-size: 12px;
          line-height: 1;
          padding-top: 4px;
        }
      }
      .footer-btn {
        .btn {
        }

        .cancel {
        }
        .submit {
        }
      }
    }
  }

  .submit-accept-success-dialog {
    ::v-deep {
      .dialog-content {
        border-top-color: var(--custom-color);
        .body-content {
          max-height: 40dvh;
          max-height: 40vh;
        }
      }
    }
    .accept-success-container {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;

      .result-icon {
        font-size: 50px;
        color: #10aa64;
        margin-right: 10px;
      }

      .title-label {
        margin-top: 16px;
        font-size: 20px;
        font-weight: 500;
        color: #303133;
        line-height: 30px;
        word-break: break-word;
        white-space: normal;
        text-align: center;
      }
      .title-tips {
        margin-top: 10px;
        height: 22px;
        font-size: 12px;
        font-weight: 400;
        color: #606266;
        line-height: 22px;
      }

      .button {
        width: 260px;
        height: 40px;
        margin: 0;
        border-radius: 2px;
      }

      .view-offer-letter {
        margin-top: 26px;
      }

      .download-offer {
        margin-top: 12px;
        margin-bottom: 6px;
        color: #0096cc;

        &:hover {
          border: 1px solid #0096cc;
        }

        .icon {
          margin-left: 4px;
        }
      }
    }
  }

  .submit-reject-success-dialog {
    ::v-deep {
      .dialog-content {
        border-top-color: var(--custom-color);
        .body-content {
          max-height: 40dvh;
          max-height: 40vh;
        }
      }
    }
    .reject-success-container {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;

      .result-icon {
        font-size: 50px;
        color: #10aa64;
        margin-right: 10px;
      }

      .title-label {
        margin-top: 16px;
        font-size: 20px;
        font-weight: 500;
        color: #303133;
        line-height: 30px;
        word-break: break-word;
        white-space: normal;
        text-align: center;
      }
      .title-tips {
        margin-top: 10px;
        height: 22px;
        font-size: 12px;
        font-weight: 400;
        color: #606266;
        line-height: 22px;
      }

      .button {
        width: 260px;
        height: 40px;
        margin: 0;
        border-radius: 2px;
      }

      .view-offer-letter {
        margin-top: 26px;
      }
    }
  }
}
</style>
